import { OutletContextProps } from 'core/contexts/outlet.context';
import { UserCustomFilter } from 'core/utils/types/user-custom-filter.type';
import { MutableRefObject, useEffect } from 'react';
import { Params, useOutletContext, useParams } from 'react-router-dom';
import { FilterCacheEntry, filterCache } from './create-filter-cache.store';
import { getFilterFromUrl, isDefaultInactiveView } from './create-filter-context.business';

export interface FilterUrlSyncConfig<T> {
    defaultFilters: { active: UserCustomFilter<T>; inactive: UserCustomFilter<T> };
    setNewOriginal: (filter: UserCustomFilter<T>) => void;
    restoreFromCache: (originalFilter: UserCustomFilter<T>, currentFilter: UserCustomFilter<T>) => void;
    moduleKey?: string;
    cacheEnabledRef?: MutableRefObject<boolean>;
}

// Applies a filter that should NOT be cached (saved filter from URL or deactivate view):
// clears any existing cache for the module and disables further cache writes.
function applyExclusiveView<T>(
    filter: UserCustomFilter<T>,
    config: Pick<FilterUrlSyncConfig<T>, 'setNewOriginal' | 'moduleKey' | 'cacheEnabledRef'>
) {
    const { setNewOriginal, moduleKey, cacheEnabledRef } = config;
    if (moduleKey) {
        filterCache.clearModule(moduleKey);
    }
    if (cacheEnabledRef) {
        cacheEnabledRef.current = false;
    }
    setNewOriginal(filter);
}

// Restores both originalFilter and currentFilter from cache, preserving the "modified"
// relationship between them (a single setter would collapse the two).
function applyCachedFilter<T>(cached: FilterCacheEntry, config: Pick<FilterUrlSyncConfig<T>, 'restoreFromCache'>) {
    config.restoreFromCache(cached.originalFilter as UserCustomFilter<T>, cached.currentFilter as UserCustomFilter<T>);
}

function resolveFilterFromUrl<T>(
    window_location_params: Readonly<Params<string>>,
    sideMenuEntries: UserCustomFilter<unknown>[],
    config: FilterUrlSyncConfig<T>
) {
    const resolvedFilter = getFilterFromUrl<T>(window_location_params.filter, sideMenuEntries);
    if (resolvedFilter) {
        applyExclusiveView(resolvedFilter, config);
        return;
    }

    if (isDefaultInactiveView(window_location_params)) {
        applyExclusiveView(config.defaultFilters.inactive, config);
        return;
    }

    // Base URL: enable caching, then restore from cache if any, else fall back to active default.
    if (config.cacheEnabledRef) {
        config.cacheEnabledRef.current = true;
    }
    const cached = config.moduleKey ? filterCache.getCachedFilter(config.moduleKey) : undefined;
    if (cached) {
        applyCachedFilter(cached, config);
        return;
    }
    config.setNewOriginal(config.defaultFilters.active);
}

export function useFilterUrlSync<T>(config: FilterUrlSyncConfig<T>) {
    const { sideMenuEntries } = useOutletContext<OutletContextProps>();
    const window_location_params = useParams();

    useEffect(() => {
        resolveFilterFromUrl(window_location_params, sideMenuEntries, config);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- config fields (defaultFilters/setNewOriginal/restoreFromCache) are stable references
    }, [window_location_params.filter, sideMenuEntries]);
}
